class BlogTableOfContents extends HTMLElement {
  constructor() {
    super();
    this.headings = [];
    this.tocItems = [];
    this.activeIndex = 0;
    this.highlightBar = null;
    this.isScrolling = false;
  }

  connectedCallback() {
    this.initialize();
    this.setupScrollListener();
    this.setupResizeListener();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.updateHighlightBar);
  }

  // Posts whose contents menu should start from the second heading (skip the intro),
  // and whose intro heading should be styled as a sub-title.
  static get SKIP_FIRST_HANDLES() {
    return ['5-easy-nourishing-recipes-with-betta-you-wholefoods-protein'];
  }

  shouldSkipFirst() {
    if (this.dataset.skipFirst === 'true') return true;
    const handle = window.location.pathname.split('/').filter(Boolean).pop();
    return BlogTableOfContents.SKIP_FIRST_HANDLES.indexOf(handle) !== -1;
  }

  initialize() {
    const articleContent = document.querySelector('.article');
    if (!articleContent) return;

    this.headings = Array.from(articleContent.querySelectorAll('h2, h3'));

    if (this.shouldSkipFirst() && this.headings.length > 0) {
      // Style the intro heading as a sub-title and drop it from the contents list.
      this.headings[0].classList.add('blog-subtitle');
      this.headings = this.headings.slice(1);
    }

    this.generateTOC();
    this.createHighlightBar();
    this.updateHighlightBar();
  }

  generateTOC() {
    if (this.headings.length === 0) {
      this.style.display = 'none';
      return;
    }

    this.headings.forEach((heading, index) => {
      if (!heading.id) {
        heading.id = `heading-${index}`;
      }
    });

    const tocHTML = this.headings.map((heading, index) => {
      const text = heading.textContent.trim();
      const level = heading.tagName.toLowerCase();
      return `
        <div class="toc-item ${level === 'h3' ? 'toc-item--sub' : ''}" data-index="${index}">
          <a href="#${heading.id}" class="toc-link">${text}</a>
        </div>
      `;
    }).join('');

    this.innerHTML = `
      <div class="toc-container">
        <div class="toc-title">Contents</div>
        <div class="toc-list">
          ${tocHTML}
        </div>
      </div>
    `;

    this.tocItems = this.querySelectorAll('.toc-item');
    this.setupTOCEventListeners();
  }

  createHighlightBar() {
    this.highlightBar = document.createElement('div');
    this.highlightBar.className = 'toc-highlight-bar';
    this.querySelector('.toc-list').appendChild(this.highlightBar);
  }

  setupTOCEventListeners() {
    this.tocItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.isScrolling = true;
        this.setActiveItem(index);
        
        const targetHeading = this.headings[index];
        const targetPosition = targetHeading.getBoundingClientRect().top + window.pageYOffset - 100;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        setTimeout(() => {
          this.isScrolling = false;
        }, 1000);
      });
    });
  }

  setupScrollListener() {
    this.handleScroll = this.throttle(() => {
      if (this.isScrolling) return;
      this.updateActiveSection();
    }, 100);

    window.addEventListener('scroll', this.handleScroll);
  }

  setupResizeListener() {
    window.addEventListener('resize', () => this.updateHighlightBar());
  }

  updateActiveSection() {
    let activeIndex = 0;
    const scrollPosition = window.pageYOffset + 150;

    for (let i = this.headings.length - 1; i >= 0; i--) {
      const heading = this.headings[i];
      if (heading.offsetTop <= scrollPosition) {
        activeIndex = i;
        break;
      }
    }

    if (activeIndex !== this.activeIndex) {
      this.setActiveItem(activeIndex);
    }
  }

  setActiveItem(index) {
    this.activeIndex = index;
    
    this.tocItems.forEach((item, i) => {
      item.classList.toggle('toc-item--active', i === index);
    });

    this.updateHighlightBar();
  }

  updateHighlightBar() {
    if (!this.highlightBar || !this.tocItems[this.activeIndex]) return;

    const activeItem = this.tocItems[this.activeIndex];
    const rect = activeItem.getBoundingClientRect();
    const containerRect = this.querySelector('.toc-list').getBoundingClientRect();

    const top = rect.top - containerRect.top;
    const height = rect.height;

    this.highlightBar.style.transform = `translateY(${top}px)`;
    this.highlightBar.style.height = `${height}px`;
  }

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }
}

window.customElements.define('blog-table-of-contents', BlogTableOfContents);